import { useState } from "react";
import { LoaderCircle } from "lucide-react";

import { createInquiry } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const initialFormState = {
  name: "",
  company_name: "",
  phone_number: "",
  email: "",
  product_interest: "Fresh Mushrooms",
  quantity_requirement: "",
  message: "",
};

export function QuoteForm({ copy, products, language, mode = "quote" }) {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const showQuoteFields = mode === "quote";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatusMessage("");

    try {
      await createInquiry({
        inquiry_type: mode,
        name: formData.name,
        company_name: showQuoteFields ? formData.company_name : null,
        phone_number: formData.phone_number,
        email: showQuoteFields ? null : formData.email || null,
        product_interest: showQuoteFields ? formData.product_interest : null,
        quantity_requirement: showQuoteFields ? formData.quantity_requirement : null,
        buyer_type: showQuoteFields ? formData.company_name || null : null,
        message: formData.message,
        language,
      });

      setFormData(initialFormState);
      setStatusMessage(copy.success);
      toast.success(copy.success);
    } catch (error) {
      const fallback = copy.error;
      setStatusMessage(fallback);
      toast.error(error?.response?.data?.detail || fallback);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="rounded-[28px] border-stone-200 bg-white shadow-xl shadow-stone-200/60">
      <CardHeader className="space-y-3">
        <CardTitle className="font-heading text-3xl text-stone-900" data-testid={`${mode}-form-title`}>
          {copy.title}
        </CardTitle>
        <p className="text-sm leading-7 text-stone-600" data-testid={`${mode}-form-description`}>
          {copy.description}
        </p>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-stone-700">{copy.fields.name}</span>
            <Input
              required
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={copy.placeholders.name}
              data-testid={`${mode}-form-name-input`}
            />
          </label>

          {showQuoteFields ? (
            <label className="grid gap-2">
              <span className="text-sm font-medium text-stone-700">{copy.fields.company}</span>
              <Input
                required
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder={copy.placeholders.company}
                data-testid={`${mode}-form-company-input`}
              />
            </label>
          ) : (
            <label className="grid gap-2">
              <span className="text-sm font-medium text-stone-700">{copy.fields.email}</span>
              <Input
                required
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={copy.placeholders.email}
                data-testid={`${mode}-form-email-input`}
              />
            </label>
          )}

          <label className="grid gap-2">
            <span className="text-sm font-medium text-stone-700">{copy.fields.phone}</span>
            <Input
              required
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder={copy.placeholders.phone}
              data-testid={`${mode}-form-phone-input`}
            />
          </label>

          {showQuoteFields ? (
            <label className="grid gap-2">
              <span className="text-sm font-medium text-stone-700">{copy.fields.product}</span>
              <select
                name="product_interest"
                value={formData.product_interest}
                onChange={handleChange}
                className="flex h-11 w-full rounded-md border border-stone-200 bg-white px-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid={`${mode}-form-product-select`}
              >
                {products.map((product) => (
                  <option key={product.value} value={product.value}>
                    {language === "hi" ? product.labelHi : product.labelEn}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {showQuoteFields ? (
            <label className="grid gap-2 md:col-span-2">
              <span className="text-sm font-medium text-stone-700">{copy.fields.quantity}</span>
              <Input
                required
                name="quantity_requirement"
                value={formData.quantity_requirement}
                onChange={handleChange}
                placeholder={copy.placeholders.quantity}
                data-testid={`${mode}-form-quantity-input`}
              />
            </label>
          ) : null}

          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-medium text-stone-700">{copy.fields.message}</span>
            <Textarea
              required
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={copy.placeholders.message}
              className="min-h-[140px]"
              data-testid={`${mode}-form-message-input`}
            />
          </label>

          {statusMessage ? (
            <div
              className="md:col-span-2 rounded-2xl border border-primary/10 bg-primary/5 px-4 py-3 text-sm text-primary"
              data-testid={`${mode}-form-status-message`}
            >
              {statusMessage}
            </div>
          ) : null}

          <div className="md:col-span-2 flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-11 rounded-full bg-accent px-6 text-accent-foreground transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:bg-accent/90"
              data-testid={`${mode}-form-submit-button`}
            >
              {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
              {showQuoteFields ? copy.submitQuote : copy.submitContact}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}